// /apps/patient/src/pages/appointments/AppointmentReschedule.tsx

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { AlertCircle } from 'lucide-react';
import { ManageAppointmentInfo } from '../../components/appointments/manageTypes';
import { ManageWindowNotice } from '../../components/appointments/ManageWindowNotice';
import { RescheduleAppointmentPanel } from '../../components/appointments/RescheduleAppointmentPanel';

export function AppointmentReschedule() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<ManageAppointmentInfo | null>(null);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const manage = await patientApiService.getAppointmentManageInfo(id);
      if (!manage) {
        setError('Appointment not found');
        return;
      }
      setInfo(manage);
    } catch (err) {
      console.error('Error loading appointment data:', err);
      setError('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const backToDetail = () => navigate(`/appointments/${id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Reschedule Appointment" showBack />
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-3" />
            <p className="text-red-800 mb-4">{error || 'Appointment not found'}</p>
            <button
              onClick={backToDetail}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Back to Appointment
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!info.can_reschedule) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Reschedule Appointment" showBack />
        <div className="p-4 space-y-4">
          <ManageWindowNotice
            windowHours={info.cancellation_window_hours}
            phone={info.organization_phone}
            blockedByType={info.blocked_by_type}
          />
          <button
            onClick={backToDetail}
            className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back to Appointment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Reschedule Appointment" showBack />
      <div className="p-4 pb-8">
        <RescheduleAppointmentPanel
          info={info}
          onSubmit={(body) => patientApiService.rescheduleAppointment(id, body)}
          onJoinWaitlist={(body) => patientApiService.joinWaitlist(id, body)}
          onDone={backToDetail}
        />
      </div>
    </div>
  );
}
